/**
 * Marine vessel detail panel (L.2.2, R.1d render). Mirrors the Aircraft detail
 * panel — name, MMSI/IMO/call sign, vessel type + AIS class, last seen + position,
 * operator org — with the R.0.5 "illustrative / best-effort" caveat and a
 * freshness note keyed to ais_visibility.
 */
import { useEffect, useState } from 'react';
import { fetchVesselDetail, type VesselDetail, type VesselFeature } from './api';
import { classifyVessel, VESSEL_AMBIENT_COLOR } from './vesselStyles';
import { resolveVesselSilhouette, VESSEL_LABEL } from './vesselSilhouettes';
import { logAction } from './lib/operatorActions';

function agoLabel(iso: string | null): string {
  if (!iso) return 'never';
  const ms = Date.now() - Date.parse(iso);
  if (!Number.isFinite(ms)) return 'unknown';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function MarineVesselBody({ vessel }: { vessel: VesselFeature }): JSX.Element {
  const p = vessel.properties;
  const [detail, setDetail] = useState<VesselDetail | null>(null);
  const style = classifyVessel(p);
  const label = VESSEL_LABEL[resolveVesselSilhouette(p.vessel_type)];
  const [lon, lat] = vessel.geometry.coordinates;

  useEffect(() => {
    logAction('view_marine_vessel_detail', {
      vessel_id: vessel.id,
      mmsi: p.mmsi,
      vessel_type: p.vessel_type,
    });
  }, [vessel.id, p.mmsi, p.vessel_type]);

  useEffect(() => {
    if (!p.mmsi) return undefined;
    const ctl = new AbortController();
    void fetchVesselDetail(p.mmsi, ctl.signal)
      .then(setDetail)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [p.mmsi]);

  return (
    <div className="px-4 py-4 space-y-4" data-testid="marine-vessel-body">
      <div className="border-l-2 pl-3" style={{ borderColor: VESSEL_AMBIENT_COLOR }}>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">Marine vessel</p>
        <h2 className="text-base font-semibold">{p.vessel_name ?? p.mmsi ?? 'Unknown vessel'}</h2>
        <p className="text-xs text-slate-400 mt-0.5">{label}</p>
        {style.stale && (
          <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px]">
            live feed stale
          </span>
        )}
      </div>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Identity</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">MMSI</dt>
          <dd className="font-mono">{p.mmsi ?? '—'}</dd>
          {detail?.properties.imo && (
            <>
              <dt className="text-slate-400">IMO</dt>
              <dd className="font-mono">{detail.properties.imo}</dd>
            </>
          )}
          {detail?.properties.call_sign && (
            <>
              <dt className="text-slate-400">Call sign</dt>
              <dd className="font-mono">{detail.properties.call_sign}</dd>
            </>
          )}
          <dt className="text-slate-400">AIS class</dt>
          <dd className="font-mono">{p.ais_class ?? '—'}</dd>
          <dt className="text-slate-400">Operator</dt>
          <dd>{detail?.properties.operator_asset_name ?? '—'}</dd>
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-1.5">Live position</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Last seen</dt>
          <dd>{agoLabel(p.last_seen_at)}</dd>
          <dt className="text-slate-400">Speed</dt>
          <dd className="font-mono">{p.speed_kt != null ? `${p.speed_kt.toFixed(1)} kt` : '—'}</dd>
          <dt className="text-slate-400">Heading</dt>
          <dd className="font-mono">{p.heading_deg != null ? `${Math.round(p.heading_deg)}°` : '—'}</dd>
          <dt className="text-slate-400">Nav status</dt>
          <dd>{p.nav_status ?? '—'}</dd>
          <dt className="text-slate-400">Position</dt>
          <dd className="font-mono text-xs">
            {lat.toFixed(3)}, {lon.toFixed(3)}
          </dd>
          <dt className="text-slate-400">AIS visibility</dt>
          <dd>{p.ais_visibility ?? '—'}</dd>
          {p.primary_waters && (
            <>
              <dt className="text-slate-400">Waters</dt>
              <dd>{p.primary_waters}</dd>
            </>
          )}
          {p.homeport && (
            <>
              <dt className="text-slate-400">Homeport</dt>
              <dd>{p.homeport}</dd>
            </>
          )}
        </dl>
      </section>

      <div className="border border-amber-500/30 bg-amber-500/10 rounded px-3 py-2 text-[11px] text-amber-200/90 leading-relaxed">
        Illustrative / best-effort. AIS positions are sourced from a beta feed with
        no published terms or SLA; Class-B and AIS-dark gaps are normal, so a sparse
        or absent fix is not &ldquo;offline&rdquo; (R.0.5).
      </div>
    </div>
  );
}
